<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { usePPA } from '@/composables/eos/ppa'
import { currencyFormatter, megawattFormatter } from '@/helpers/ep/formatters'
import { SiteType } from '@/types/core/Site'
import type { IPPAFilters } from '@/types/eos/filters'
import type { IPPAForm } from '@/types/eos/forms'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { type FormInstance, type FormRules } from 'element-plus'
import { DateTime } from 'luxon'
import { reactive, ref } from 'vue'

const selectedSiteRef = ref<ISiteIdentifier>()

const filters = reactive<IPPAFilters>({
  site: undefined,
  active_only: undefined,
})

const { getPPAsQuery, createPPAMutation, deletePPAMutation } = usePPA()
const { data, isPending, isError, isRefetching, error, refetch } = getPPAsQuery(filters)

const createPPADialogVisible = ref<boolean>(false)
const createPPAFormRef = ref<FormInstance>()
const createPPASiteSelectRef = ref<ISiteIdentifier>()
const createPPAFormDateRange = ref<[Date, Date]>([
  DateTime.now().toJSDate(),
  DateTime.now().plus({ months: 1 }).toJSDate(),
])
const createPPAForm = reactive<IPPAForm>({
  site_id: undefined,
  capacity: undefined,
  price_usd: undefined,
  start_date: DateTime.now().toFormat('yyyy-MM-dd'),
  end_date: DateTime.now().plus({ months: 1 }).toFormat('yyyy-MM-dd'),
})

/**
 * Validates `capacity` form input.
 */
const validateCapacity = (rule: any, value: any, callback: any) => {
  value = Number(value)
  if (value === '') {
    callback(new Error('Please enter capacity (must be > 0).'))
  } else {
    if (Number.isNaN(value)) {
      callback(new Error('Capacity must be a number.'))
    } else if (value <= 0) {
      callback(new Error('Capacity must be a positive non-zero number.'))
    }
    callback()
  }
}

/**
 * Validates `capacity` form input.
 */
const validatePrice = (rule: any, value: any, callback: any) => {
  value = Number.parseInt(value)
  if (value === '') {
    callback(new Error('Please enter price (must be at least 0).'))
  } else {
    if (Number.isNaN(value)) {
      callback(new Error('Price must be a number.'))
    } else if (value < 0) {
      callback(new Error('Price must be at least 0.'))
    }
    callback()
  }
}

const createPPAFormRules = reactive<FormRules<IPPAForm>>({
  start_date: [{ required: true, type: 'string', message: 'Please enter a start date.' }],
  end_date: [{ required: true, type: 'string', message: 'Please enter an end date.' }],
  site_id: [{ required: true, type: 'string', message: 'Please select a site.' }],
  capacity: [
    {
      required: true,
      type: 'number',
      min: 0.001,
      validator: validateCapacity,
      trigger: ['blur', 'change'],
    },
  ],
  price_usd: [
    {
      required: true,
      type: 'number',
      min: 0.0,
      validator: validatePrice,
      trigger: ['blur', 'change'],
    },
  ],
})

const handleCreatePPADialogCancel = (formEl: FormInstance | undefined) => {
  if (!formEl) return
  createPPADialogVisible.value = false
  resetCreatePPAForm()
}

const handleCreatePPADialogConfirm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  await formEl.validate(async (valid, fields) => {
    if (valid) {
      const newPPA: IPPAForm = {
        site_id: createPPAForm.site_id!,
        capacity: createPPAForm.capacity!,
        price_usd: createPPAForm.price_usd!,
        start_date: createPPAForm.start_date!,
        end_date: createPPAForm.end_date!,
      }

      try {
        await createPPAMutation.mutateAsync(newPPA)
        createPPADialogVisible.value = false
        resetCreatePPAForm()
      } catch {
        // Handled by composable
      }
    }
  })
}

const onSiteChange = (val: ISiteIdentifier) => {
  if (val) {
    selectedSiteRef.value = val
    filters.site = val.site_id
    return
  }

  filters.site = undefined
  selectedSiteRef.value = undefined
}

const onCreatePPASiteSelectChange = (val: ISiteIdentifier) => {
  if (val) {
    createPPAForm.site_id = val.site_id!
    return
  }

  createPPAForm.site_id = undefined
}

const onCreatePPAFormDateRangeChange = (val: Date[]) => {
  if (val.length === 2) {
    createPPAForm.start_date = DateTime.fromJSDate(val[0]).toFormat('yyyy-MM-dd')!
    createPPAForm.end_date = DateTime.fromJSDate(val[1]).toFormat('yyyy-MM-dd')!
    return
  }

  createPPAForm.start_date = undefined
  createPPAForm.end_date = undefined
}

const resetCreatePPAForm = () => {
  createPPAFormRef.value?.resetFields()
  createPPAFormDateRange.value = [new Date(), new Date()]
  createPPASiteSelectRef.value = undefined
}
</script>

<template>
  <ViewLayout>
    <template v-if="!isError" #controls>
      <el-button type="primary" :disabled="isPending" @click="createPPADialogVisible = true">Add PPA</el-button>
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="() => refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <RcSiteSelect
        v-model="selectedSiteRef"
        style="width: 256px"
        :type="SiteType.DataCenter"
        placeholder="Site"
        @change="onSiteChange"
      />
      <el-table v-loading="isPending" :data="data" empty-text="No PPA's found." :stripe="true">
        <el-table-column label="Start Date" prop="start_datetime" :min-width="128">
          <template #default="scope">
            <div style="display: flex; align-items: center">
              {{ DateTime.fromISO(scope.row.start_datetime).toLocaleString(DateTime.DATE_SHORT) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="End Date" prop="end_datetime" :min-width="128">
          <template #default="scope">
            <div style="display: flex; align-items: center">
              {{ DateTime.fromISO(scope.row.end_datetime).toLocaleString(DateTime.DATE_SHORT) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Site" prop="site" :min-width="128">
          <template #default="scope">
            <el-tag>{{ scope.row.site }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Price (USD)" prop="price_usd" :formatter="currencyFormatter" :min-width="128" />
        <el-table-column label="Position (MW)" prop="capacity" :min-width="128" :formatter="megawattFormatter">
        </el-table-column>
        <el-table-column align="right">
          <template #default="scope">
            <el-button link>
              <Icon
                icon="material-symbols:delete-outline"
                :width="20"
                :height="20"
                @click="
                  DeleteConfirmationModal(
                    'Delete PPA',
                    'Are you sure you want to delete this PPA? This action cannot be undone',
                    scope.row.id,
                    deletePPAMutation.mutateAsync
                  )
                "
            /></el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-dialog
        v-model="createPPADialogVisible"
        title="Create PPA"
        :width="480"
        :show-close="false"
        @closed="resetCreatePPAForm"
      >
        <el-form
          ref="createPPAFormRef"
          :label-width="160"
          label-position="left"
          :model="createPPAForm"
          :rules="createPPAFormRules"
        >
          <el-form-item label="Date Range" required>
            <el-date-picker
              v-model="createPPAFormDateRange"
              type="daterange"
              start-placeholder="Start date"
              end-placeholder="End date"
              range-separator="To"
              @change="onCreatePPAFormDateRangeChange"
            />
          </el-form-item>
          <el-form-item label="Site" prop="site_id" required>
            <RcSiteSelect
              v-model="createPPASiteSelectRef"
              style="width: 100%"
              :type="SiteType.DataCenter"
              placeholder="Site"
              @change="onCreatePPASiteSelectChange"
            />
          </el-form-item>
          <el-form-item label="Capacity (MWh)" prop="capacity" required>
            <el-input-number
              v-model="createPPAForm.capacity"
              style="width: 100%"
              :precision="3"
              :min="0.001"
              controls-position="right"
              placeholder="Capacity"
            />
          </el-form-item>
          <el-form-item label="Price (USD)" prop="price_usd" required>
            <el-input-number
              v-model="createPPAForm.price_usd"
              style="width: 100%"
              :precision="2"
              :min="0.0"
              placeholder="Price"
              controls-position="right"
            />
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="handleCreatePPADialogCancel(createPPAFormRef)">Cancel</el-button>
          <el-button type="primary" @click="handleCreatePPADialogConfirm(createPPAFormRef)">Save</el-button>
        </template>
      </el-dialog>
    </template>
  </ViewLayout>
</template>
